import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import './public-apply.css';

// Public-facing inquiry form. The school shares the URL of this page on
// their Instagram / WhatsApp / website. The page reads its tenant
// context from the subdomain (or `?tenant=CODE` in dev) and posts to
// /api/admissions/inquiry — landing the lead at the top of the funnel.
const SOURCES = [
  { id: 'walk-in',     label: 'Walked in to the school' },
  { id: 'phone',       label: 'Called the school' },
  { id: 'website',     label: 'Found the school website' },
  { id: 'referral',    label: 'Referred by a friend / family' },
  { id: 'instagram',   label: 'Instagram' },
  { id: 'facebook',    label: 'Facebook' },
  { id: 'google',      label: 'Google search' },
  { id: 'event',       label: 'School event / open day' },
  { id: 'other',       label: 'Somewhere else' }
];

export default function PublicApplyPage() {
  const [tenant, setTenant] = useState(null);
  const [tenantLoaded, setTenantLoaded] = useState(false);
  const [form, setForm] = useState({
    firstName: '', lastName: '',
    fatherName: '', motherName: '',
    contactPhone: '', contactEmail: '',
    applyingForClass: '',
    previousSchool: '',
    inquirySource: 'website',
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submittedRef, setSubmittedRef] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/admissions/public/school');
        if (!cancelled) setTenant(data?.school || null);
      } catch {
        if (!cancelled) setTenant(null);
      } finally {
        if (!cancelled) setTenantLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const change = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    setErr(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErr(null);
    try {
      const { data } = await api.post('/admissions/inquiry', form);
      setSubmittedRef(data.reference);
    } catch (e2) {
      setErr(e2?.response?.data?.message || e2?.response?.data?.errors?.[0]?.msg || 'Could not submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!tenantLoaded) {
    return <div className="ap-loading">Loading…</div>;
  }
  if (!tenant) {
    return (
      <div className="ap-page">
        <div className="ap-card">
          <h1>Open the school's apply link</h1>
          <p>This page only works on a school's branded subdomain. Please use the URL shared by the school's office.</p>
        </div>
      </div>
    );
  }

  if (submittedRef) {
    return (
      <div className="ap-page">
        <div className="ap-card ap-success">
          <div className="ap-success-icon">✓</div>
          <h1>Thanks — we received your inquiry.</h1>
          <p>The {tenant.name} office will reach out to you on the phone you provided.</p>
          <p className="ap-ref">Reference: <code>#{submittedRef}</code></p>
          <p className="ap-fineprint">Please save this reference. The school may ask for it when they call back.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="ap-page">
      <div className="ap-shell">
        <header className="ap-header">
          <div>
            <div className="ap-eyebrow">Admission inquiry</div>
            <h1 className="ap-title">{tenant.name}</h1>
            <p className="ap-sub">Tell us about your child. The school office will call you within 24 hours.</p>
          </div>
        </header>

        <form className="ap-form" onSubmit={submit}>
          <section className="ap-section">
            <h3>Child</h3>
            <div className="ap-grid">
              <label className="ap-field">
                <span>First name *</span>
                <input name="firstName" required value={form.firstName} onChange={change} />
              </label>
              <label className="ap-field">
                <span>Last name *</span>
                <input name="lastName" required value={form.lastName} onChange={change} />
              </label>
              <label className="ap-field">
                <span>Applying for class *</span>
                <select name="applyingForClass" required value={form.applyingForClass} onChange={change}>
                  <option value="">— pick a class —</option>
                  {['Pre-KG','LKG','UKG','1','2','3','4','5','6','7','8','9','10','11','12'].map((c) =>
                    <option key={c} value={c}>{c.length <= 3 && /^\d/.test(c) ? `Class ${c}` : c}</option>
                  )}
                </select>
              </label>
              <label className="ap-field">
                <span>Previous school</span>
                <input name="previousSchool" value={form.previousSchool} onChange={change} placeholder="If currently enrolled" />
              </label>
            </div>
          </section>

          <section className="ap-section">
            <h3>Parents</h3>
            <div className="ap-grid">
              <label className="ap-field">
                <span>Father's name</span>
                <input name="fatherName" value={form.fatherName} onChange={change} />
              </label>
              <label className="ap-field">
                <span>Mother's name</span>
                <input name="motherName" value={form.motherName} onChange={change} />
              </label>
              <label className="ap-field">
                <span>Contact phone *</span>
                <input type="tel" name="contactPhone" required value={form.contactPhone} onChange={change} placeholder="+91 …" />
              </label>
              <label className="ap-field">
                <span>Contact email</span>
                <input type="email" name="contactEmail" value={form.contactEmail} onChange={change} />
              </label>
            </div>
          </section>

          <section className="ap-section">
            <h3>How did you hear about us?</h3>
            <div className="ap-source-grid">
              {SOURCES.map((s) => (
                <label key={s.id} className={`ap-source ${form.inquirySource === s.id ? 'on' : ''}`}>
                  <input
                    type="radio"
                    name="inquirySource"
                    value={s.id}
                    checked={form.inquirySource === s.id}
                    onChange={change}
                  />
                  {s.label}
                </label>
              ))}
            </div>
          </section>

          <section className="ap-section">
            <h3>Anything you'd like the school to know?</h3>
            <textarea
              name="notes"
              rows={4}
              value={form.notes}
              onChange={change}
              placeholder="Optional — special needs, sibling already in school, preferred call time, etc."
            />
          </section>

          {err && <div className="ap-err">{err}</div>}

          <div className="ap-actions">
            <button type="submit" disabled={submitting} className="ap-submit">
              {submitting ? 'Submitting…' : 'Submit inquiry'}
            </button>
          </div>
          <p className="ap-disclaimer">
            By submitting, you agree to be contacted by {tenant.name} regarding admission.
            See <a href="/privacy">Privacy</a> and <a href="/terms">Terms</a>.
          </p>
        </form>
      </div>
    </div>
  );
}
